import React from "react";
import { withTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { reportClientError } from "@/lib/errorReporter";

/**
 * Per-route Error Boundary. Renders an in-flow fallback (NOT full-screen) so
 * the sidebar + top bar remain usable when a single page crashes. Resets when
 * `resetKey` changes — typically wired to `location.pathname` so navigating
 * to another route via the sidebar clears the error.
 *
 * Complementa el top-level `ErrorBoundary` (full-screen) que sigue cubriendo
 * crashes en contextos / providers / layout shell.
 */
class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    reportClientError({
      kind: "frontend_react_route_crash",
      severity: "error",
      message: error?.message || "Route render crash",
      stack: (error?.stack || "") + "\n\nComponent stack:\n" + (errorInfo?.componentStack || ""),
    });
    Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo?.componentStack } },
      tags: { boundary: "route" },
    });
  }

  componentDidUpdate(prevProps) {
    // Reset on route change so navigating away clears the error state.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    const { t } = this.props;
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center py-16 px-4">
          <div className="bg-[#1e293b] border border-slate-700 rounded-2xl p-6 sm:p-8 max-w-md w-full text-center">
            <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-6 h-6 text-amber-400" />
            </div>
            <h2 className="text-lg font-semibold text-white mb-2">
              {t("routeErrorBoundary.title")}
            </h2>
            <p className="text-sm text-slate-400 mb-5">{t("routeErrorBoundary.message")}</p>
            <button
              onClick={this.handleRetry}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#0052FF] hover:bg-[#0040d0] text-white text-sm font-medium rounded-xl transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              {t("routeErrorBoundary.retry")}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default withTranslation()(RouteErrorBoundary);
